import { collectBuffers } from './collectBuffers.js'
import { ABANDON_AFTER_MS } from './constants.js'
import { createGridRuns, trapped } from './gridRun.js'
import { createPool, NEEDS_SOLIDS } from './workerPool.js'
import { createSlots } from './workerSlot.js'

export { ABANDON_AFTER_MS } from './constants.js'
export const DEFAULT_TIMEOUT_MS = 30000

const RESPONSE = '__RESPONSE__'

// '@jscad/modeling' resolves to the engine's anchored tree bundle;
// '@jscad/modeling-for-manifold' stays the plain bundle that twin wraps.
export const workerBundles = (bundleBase, engine) => ({
  '@jscad/modeling': bundleBase + (engine === 'manifold' ? 'bundle.model_tree_anchored_manifold.js' : 'bundle.model_tree_anchored_jscad.js'),
  '@jscad/modeling-for-anchors': bundleBase + (engine === 'manifold' ? 'bundle.model_tree_anchored_manifold.js' : 'bundle.model_tree_anchored_jscad.js'),
  '@jscad/modeling-for-manifold': bundleBase + 'bundle.jscad_modeling.js',
  '@jbroll/jscad-anchors': bundleBase + 'bundle.jscad-anchors.js',
  '@jscad/io': bundleBase + 'bundle.jscad_io.js',
  '@jscadui/model-tools': bundleBase + 'bundle.model-tools.js',
  '@jbroll/jscad-fluent': bundleBase + 'bundle.jscad-fluent.js',
  '@jscad/csg': bundleBase + 'bundle.V1_api.js',
  '@jscadui/params-core': bundleBase + 'bundle.params_core.js',
  '@jscadui/jscad-text': bundleBase + 'bundle.jscad_text.js',
})

// Memory bounds the pool, not cores: each worker holds up to a 1 GiB WASM heap,
// and four of them sent a 15 GB laptop into swap where leaves ran 100x slower.
export const defaultPoolSize = (hardwareConcurrency = 2) => Math.max(1, Math.min(hardwareConcurrency - 1, 2))

/**
 * The frame's side of the relayed protocol: a pool of workers, a timeout per
 * in-flight request, grid runs spread across the pool, and jscadInit
 * rewritten so bundle URLs come from here rather than from the sender.
 *
 * @param {object} options
 * @param {string} options.allowedOrigin the app origin; the only sender answered
 * @param {string} options.bundleBase absolute base the frame's own bundles live under
 * @param {string[]} [options.libraries] deployed library directory names under /libs/
 * @param {() => Worker} options.createWorker
 * @param {(message: unknown, transfer?: Transferable[]) => void} options.post sends to the app
 * @param {Window} options.parentWindow the only window whose messages are accepted
 * @param {() => string} [options.randomId]
 * @param {number} [options.hardwareConcurrency]
 */
export const createFrameHost = ({
  allowedOrigin,
  bundleBase,
  libraries = [],
  createWorker,
  post,
  parentWindow,
  randomId = () => crypto.randomUUID(),
  hardwareConcurrency = globalThis.navigator?.hardwareConcurrency,
}) => {
  // Latched: a jscadInit that omits `engine` keeps the last one. The alias
  // path (onAliasFound) re-inits without naming an engine and must not switch
  // the model bundles out from under a loaded project.
  let engine
  /** @type {import('./workerPool.js').State} */
  const state = {
    slots: [],
    active: null,
    // What a new worker is set up with, in the order the app sent it.
    mirrored: [],
    lastScript: undefined,
    // The newest script the app sent, answered or not.
    sentScript: undefined,
    // Source of the last loaded non-scratch script; a sourced load from a
    // different source retires the pool so one project never sees another's.
    lastSource: undefined,
    lastMain: undefined,
    poolSize: defaultPoolSize(hardwareConcurrency),
    timeoutMs: DEFAULT_TIMEOUT_MS,
  }

  const answerError = (id, name, message) =>
    post({ method: RESPONSE, id, error: { name, message } })

  const slotOps = createSlots({
    createWorker,
    randomId,
    timeoutMs: () => state.timeoutMs,
    answerError,
    onMessage: (slot, data) => receive(slot, data),
    onKill: (slot, expiredId, reason, errorName) => pool.kill(slot, expiredId, reason, errorName),
    onEnd: (slot, errorName) => runs.leave(slot, errorName),
  })
  const pool = createPool({
    state,
    slotOps,
    post,
    answerError,
    busy: (slot) => runs.busy(slot),
    inGrid: (slot) => runs.inGrid(slot),
    openRun: (slot, message, entry) => runs.open(slot, message, entry),
    onExit: (slot, reason) => dropHeld(slot, 'AbortError', `worker terminated before this request finished: ${reason}`),
  })
  const runs = createGridRuns({ state, pool, slotOps, post, answerError })

  // Only the solids re-run of export, measure and check posts progress; relaying
  // it elsewhere would let model code keep any request alive. A reload for one
  // of those counts too: its steps run as frame requests while the app's own
  // request waits behind them.
  const exporting = (slot) => [...slot.pending.values()].some((r) => !r.onAnswer && NEEDS_SOLIDS.has(r.method)) ||
    (slot.queued ?? []).some(({ entry }) => entry && !entry.onAnswer && NEEDS_SOLIDS.has(entry.method))

  const relayOut = (slot, data) => {
    slotOps.restartTimers(slot)
    const message = { method: data.method, params: data.params }
    post(message, collectBuffers(message))
  }

  // The worker sends answers, claims, and streamed cells and progress, so
  // anything else it posts, and any answer to a request the frame did not
  // issue, is model code talking.
  const receive = (slot, data) => {
    if (data?.method === 'jscadClaim' && data.id != null) return runs.claim(slot, data)
    if (data?.id == null && data?.method === 'jscadCells' && runs.relaysCells(slot, data)) return relayOut(slot, data)
    // Cells a reload streams prove the model is advancing but belong to no run
    // the app waits on: restart the kill timer without relaying them.
    if (data?.id == null && data?.method === 'jscadCells' && slot === state.active && exporting(slot)) {
      slotOps.restartTimers(slot)
      return
    }
    if (data?.id == null && data?.method === 'jscadProgress' && slot === state.active && exporting(slot)) return relayOut(slot, data)
    if (data?.method !== RESPONSE) return
    const request = slot.pending.get(data.id)
    if (!request) return
    clearTimeout(request.timer)
    slot.pending.delete(data.id)
    if (request.setup) slot.setupAnswers.set(request.setup, data)
    if (request.onAnswer) return request.onAnswer(data)
    if (request.run?.fanned) return runs.answered(request.run, slot, data)
    if (request.run) runs.close(request.run)
    const message = { ...data, id: request.appId }
    post(message, collectBuffers(message))
    answered(slot, request, data)
    // A frame request that traps still answers the one it was made for; the
    // next app run that traps retires the worker.
    if (slot === state.active && trapped(data)) pool.retire(slot, 'the model trapped in WebAssembly')
  }

  const answered = (slot, { method, options, onAnswer, files: sentFiles }, data) => {
    // The export replay runs what the user last set, even when that run
    // failed: replaying older successful params would export stale state.
    if (method === 'jscadMain' && options) state.lastMain = options
    if (method !== 'jscadScript') return
    if (data.params?.scratch) {
      // The worker kept the loaded model, so every worker keeps its file map too.
      if (state.sentScript === options) state.sentScript = state.lastScript
      if (sentFiles === mirroredFiles()) restoreFiles()
    } else if (!data.error) {
      state.lastScript = options
      state.lastSource = options?.source
      state.lastMain = undefined
      slot.script = options
      modelFiles = mirroredFiles()
    } else if (state.sentScript === options) state.sentScript = state.lastScript
    if (!onAnswer) flushHeld(slot)
    pool.ensureSpare()
  }

  // A superseding run that arrives while a load is pending queues behind the
  // load instead of running in full on the worker; a newer one supersedes it.
  const heldMains = new Map()

  const holdsLoad = (slot) => [...slot.pending.values()]
    .some((r) => r.method === 'jscadScript' && !r.onAnswer && !r.run?.fanned)

  const flushHeld = (slot) => {
    const held = heldMains.get(slot)
    if (!held?.length) return
    heldMains.delete(slot)
    for (const { message, entry } of held) {
      if (entry) entry.script = state.sentScript
      pool.relay(slot, message, entry)
    }
  }

  const dropHeld = (slot, name, message) => {
    const held = heldMains.get(slot)
    if (!held?.length) return
    heldMains.delete(slot)
    for (const { entry } of held) {
      if (entry) answerError(entry.appId, name, message)
    }
  }

  const RECORDED = new Set(['jscadScript', 'jscadMain'])

  // A run never abandons a load: the promoted worker would reload the previous
  // script and run the new parameters against it.
  const abandonStale = (method) => {
    const slot = state.active
    const appRequests = [...slot.pending].filter(([, r]) => !r.onAnswer && !r.run?.fanned)
    if (method === 'jscadMain' && (appRequests.some(([, r]) => r.method === 'jscadScript') || runs.loadsGrid())) return
    const stale = appRequests.filter(([, r]) => RECORDED.has(r.method))
    const now = Date.now()
    if (!stale.some(([, r]) => now - r.startedAt >= ABANDON_AFTER_MS)) return
    // A younger run queued behind the stale one is replaced too.
    for (const [workerId, { appId, timer }] of stale) {
      clearTimeout(timer)
      slot.pending.delete(workerId)
      answerError(appId, 'SupersededError', 'superseded by a newer run')
    }
    // A run held behind the load was superseded by the same newer run.
    dropHeld(slot, 'SupersededError', 'superseded by a newer run')
    // An export, measure or check in flight is never superseded: retire would
    // answer it AbortError, so the new run queues behind it instead.
    if (appRequests.some(([, r]) => NEEDS_SOLIDS.has(r.method))) return
    pool.retire(slot, 'a newer run superseded the model')
  }

  // Runs waiting behind a reload have not started, so a newer run replaces
  // them without a retire. A queued script stays, as a pending one does, and so
  // does a run an export, measure or check queued after it will read.
  const supersedeQueued = () => {
    const slot = state.active
    if (!slot.queued) return
    const lastRead = slot.queued.findLastIndex(({ message }) => NEEDS_SOLIDS.has(message.method))
    slot.queued = slot.queued.filter(({ message, entry }, i) => {
      if (message.method !== 'jscadMain' || i < lastRead || entry?.onAnswer) return true
      if (entry) answerError(entry.appId, 'SupersededError', 'superseded by a newer run')
      return false
    })
  }

  const takeSupersede = (message) => {
    const [options, ...rest] = message?.params ?? []
    if (options === null || typeof options !== 'object' || !Object.hasOwn(options, 'supersede')) return [message, false]
    const { supersede, ...kept } = options
    return [{ ...message, params: [kept, ...rest] }, supersede === true]
  }

  const MIRRORED = new Set(['jscadInit', 'jscadSetFiles', 'jscadClearTempCache', 'jscadClearFileCache'])

  const setupOf = new WeakMap()

  const mirroredFiles = () => state.mirrored.find((m) => m.method === 'jscadSetFiles')
  // The file map the loaded model was answered with.
  let modelFiles

  const restoreFiles = () => {
    if (!modelFiles) return
    const restored = structuredClone(modelFiles)
    state.mirrored = state.mirrored.map((m) => (m.method === 'jscadSetFiles' ? restored : m))
    modelFiles = restored
    pool.mirror(restored)
    pool.relay(state.active, structuredClone(restored), null)
  }

  // A file map replaces the one before it, and the cache clears before it
  // cleared state that map already replaced.
  const mirror = (message) => {
    const { id: _id, ...setup } = message
    const kept = structuredClone(setup)
    if (kept.method === 'jscadInit') state.mirrored = state.mirrored.filter((m) => m.method !== 'jscadInit')
    if (kept.method === 'jscadSetFiles') state.mirrored = state.mirrored.filter((m) => m.method === 'jscadInit')
    state.mirrored.push(kept)
    setupOf.set(message, kept)
    pool.mirror(kept)
  }

  const entryFor = (message) => message.id
    ? {
      appId: message.id,
      method: message.method,
      options: RECORDED.has(message.method) ? structuredClone(message.params?.[0]) : undefined,
      setup: setupOf.get(message),
      // A run held behind a reload opens late, after a later load may have been sent;
      // workers that join it must load the script sent before it.
      script: message.method === 'jscadMain' ? state.sentScript : undefined,
      files: message.method === 'jscadScript' ? mirroredFiles() : undefined,
    }
    : null

  // The one method that is not relayed untouched. A script source inside the
  // frame must come from the frame's own origin, so the app names an engine
  // and the frame names the bundles.
  const frameInit = (data, options, rest) => {
    const { engine: wanted, timeoutMs: wantedTimeout, poolSize: wantedPool, ...init } = options
    if (wanted) engine = wanted
    if (wantedTimeout) state.timeoutMs = wantedTimeout
    if (Number.isInteger(wantedPool) && wantedPool > 0) state.poolSize = wantedPool
    // The worker's own origin is opaque, so it gets the app origin here; it is
    // the only base for include urls that arrive as bare pathnames.
    const params = { ...init, claims: true, bundles: workerBundles(bundleBase, engine), appOrigin: allowedOrigin, libraries }
    return { ...data, params: [params, ...rest] }
  }

  // Start the active worker if none is up. Replay the mirror: on a true
  // first start it is empty, and after a dead worker or a source-change
  // reset it restores the setup the app already sent.
  const ensureActive = (id) => {
    if (state.active) return true
    try {
      state.active = pool.start({ replay: true })
      return true
    } catch (error) {
      if (id) answerError(id, 'Error', `could not start the model worker: ${error?.message ?? error}`)
      return false
    }
  }

  const handleMessage = (event) => {
    if (event.origin !== allowedOrigin) return
    // Same origin is not the same window: another tab or frame on the app
    // origin could otherwise drive this worker.
    if (parentWindow && event.source !== parentWindow) return

    const data = event.data
    const id = data?.id
    let message = data
    if (data?.method === 'jscadInit') {
      const [options = {}, ...rest] = data.params ?? []
      // Rejecting now matters more than the message: otherwise the rewrite
      // throws here and the request burns the whole timeout unanswered.
      if (options === null || typeof options !== 'object' || Array.isArray(options)) {
        if (id) answerError(id, 'TypeError', 'jscadInit expects an options object')
        return
      }
      message = frameInit(data, options, rest)
    }
    const [relayed, supersede] = takeSupersede(message)
    message = relayed
    if (supersede && RECORDED.has(message.method)) {
      runs.supersede(message.method)
      if (state.active) {
        supersedeQueued()
        abandonStale(message.method)
      }
    }

    // Answered before ensureActive: an abort with nothing running must not
    // start a worker (idle abort stays idle). Closing the open runs first keeps
    // their members from recruiting replacements or answering after the
    // workers go; abortActive then removes the active worker and answers what
    // it held, without the frameWorkerTerminated that would make the app replay
    // the script and the last run and restart the stopped model.
    if (data?.method === 'jscadAbort' && data?.id != null) {
      const had = state.active !== null
      runs.park('AbortError', 'stopped by the user')
      pool.abortActive('stopped by the user')
      post({ method: RESPONSE, id: data.id, params: { aborted: had } })
      return
    }

    if (!ensureActive(id)) return
    if (MIRRORED.has(data?.method)) mirror(message)
    const entry = entryFor(message)
    if (entry?.method === 'jscadScript') state.sentScript = entry.options
    // Model code from one source must not observe another's files or scripts:
    // a sourced load that differs from the last loaded one ends the whole
    // pool, and the start below brings up a fresh worker for it, never
    // a promoted idle spare that ran the old source.
    if (entry?.method === 'jscadScript' && !entry.options?.scratch) {
      const source = entry.options?.source
      if (source !== undefined && state.lastSource !== undefined && source !== state.lastSource && state.active) {
        // The new load's setup (its SetFiles, sent in the same turn) is
        // already pending on the old pool: lift setup waits off before the
        // reset answers them, then hand each to the fresh worker's replayed
        // copy so the app's wait resolves instead of aborting.
        const preserved = []
        for (const slot of state.slots) {
          for (const [workerId, pending] of [...slot.pending]) {
            if (pending.setup && !pending.onAnswer && !pending.run?.fanned) {
              clearTimeout(pending.timer)
              slot.pending.delete(workerId)
              preserved.push(pending)
            }
          }
          if (slot.queued) {
            for (const queued of slot.queued) {
              if (queued.entry && queued.entry.setup && !queued.entry.onAnswer && !queued.entry.run?.fanned) {
                preserved.push(queued.entry)
              }
            }
          }
        }
        for (const slot of [...heldMains.keys()]) dropHeld(slot, 'AbortError', 'the loaded model changed')
        pool.reset('the loaded model changed')
        if (!ensureActive(id)) {
          for (const pending of preserved) {
            if (pending.appId !== undefined) answerError(pending.appId, 'AbortError', 'the loaded model changed')
          }
          return
        }
        for (const pending of preserved) {
          const copy = [...state.active.pending.values()].find((r) => r.setup === pending.setup)
          if (copy) Object.assign(copy, { appId: pending.appId, onAnswer: undefined })
          else if (pending.appId !== undefined) {
            pool.relay(state.active, { ...pending.setup }, {
              appId: pending.appId,
              method: pending.method,
              options: pending.options,
              setup: pending.setup,
            })
          }
        }
      }
    }
    if (!ensureActive(id)) return
    // The app sends a superseding run every 500 ms while a load is pending;
    // queue it behind the load instead of running each one in full, and let a
    // newer one supersede it.
    if (entry && supersede && entry.method === 'jscadMain' && !state.active.queued && holdsLoad(state.active)) {
      dropHeld(state.active, 'SupersededError', 'superseded by a newer run')
      heldMains.set(state.active, [{ message, entry }])
      return
    }
    pool.relay(state.active, message, entry)
  }

  const getPendingCount = () => {
    const slot = state.active
    const relayed = slot ? [...slot.pending.values()].filter((r) => !r.onAnswer && !r.run?.fanned).length : 0
    const queued = (slot?.queued ?? []).filter(({ entry }) => entry && !entry.onAnswer).length
    const held = [...heldMains.values()].reduce((n, list) => n + list.filter(({ entry }) => entry && !entry.onAnswer).length, 0)
    return relayed + queued + held + runs.pendingCount()
  }

  return { handleMessage, getPendingCount }
}
